import { createElement } from "react";
import noCardsIllustration from "@/assets/img/dashboards/empty-state-no-cards-animated.svg";
import noDashboardsIllustration from "@/assets/img/dashboards/empty-state-no-dashboards-animated.svg";
import { AccountRoleType } from "@/companyRoles/accountRoleTypeApiClient";
import { Result } from "@/common/common";
import { LocalDateFieldModel, StringFieldModel } from "@/common/commonForms";
import { today, toLocalDateISOString } from "@/utils/Dates";
import { accountRoleTypeApiClient } from "@/companyRoles/accountRoleTypeApiClient";
import { customFieldsApiClient } from "@/customFields/customFieldsApiClient";
import { TemplateDTO } from "@/templates/templateDomain";
import type { CustomFieldDefinition } from "@/customFields/customFieldsDomain";
import { EmptyStateProps } from "@/components-v2/EmptyState";
import { MTextInputProps } from "@/components-v2/TextInput";
import { DCEvent, navigate, showGeneralError, UpdateStoreEvent } from "@/events/eventsDomain";
import { AIClient } from "@/ai/AIAssistant";
import { ButtonProps } from "@/components-v2/Button";
import { RowAction } from "@/components-v2/DataTable";
import { Language } from "@/i18n/languages";
import { accountUserGroupLabel } from "@/enterpriseAccounts/visibility/accountVisibilityDomain";
import { TMessage, TMessageParam } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";
import {
  Dashboard,
  DashboardCardData,
  DashboardCardDefinition,
  DashboardOverview,
  DashboardStatus,
  dashboardApiClient,
} from "./dashboardApiClient";
import {
  cardTypeLabelFor,
  DashboardSettingsDialog,
  openCardDialog,
  openDashboardDialog,
  openDeleteDialog,
  withMapTemplates,
  withRoleTypes,
} from "./dashboardDialogsDomain";

export const dashboardSettingsTabs = ["dashboards", "cards"] as const;

export type DashboardSettingsTab = (typeof dashboardSettingsTabs)[number];

export type DashboardSettingsState = {
  accountId: string;
  language: Language;
  tab: DashboardSettingsTab;
  search: StringFieldModel;
  overview?: DashboardOverview;
  dialog?: DashboardSettingsDialog;
  selected?: SelectedItem;
  companies: PreviewCompany[];
  previewOrgNo?: string;
  previewGroup?: string;
  previewAsOf: LocalDateFieldModel;
  definitions: CustomFieldDefinition[];
  roleTypes: AccountRoleType[];
  mapTemplates: TemplateDTO[];
  previewData: Record<string, Result<DashboardCardData>>;
};

export type PreviewCompany = { orgNo: string; name: string };

export const asOfDateModel = (): LocalDateFieldModel =>
  new LocalDateFieldModel({ maxDate: toLocalDateISOString(today()) });

export const chosenDate = (
  hasDatePicker: boolean,
  model: LocalDateFieldModel,
): string | undefined => (hasDatePicker ? model.validatedValue : undefined);

export const commitAsOfDate = (model: LocalDateFieldModel, input: string): void => {
  const todayIso = toLocalDateISOString(today());
  model.setMaxDate(todayIso);
  model.setAndCommitInputValue(input === todayIso ? "" : input);
};

export type SelectedItem = { kind: "dashboard"; id: string } | { kind: "card"; id: string };

export const selectItem = (state: DashboardSettingsState, selected?: SelectedItem): DCEvent[] => [
  updateDashboardSettings(state.accountId, { selected }),
];

export const initDashboardSettings = (
  accountId: string,
  language: Language,
  tab: DashboardSettingsTab,
  hasTemplates: boolean,
  companies: PreviewCompany[] = [],
): DCEvent[] => [
  {
    type: "update-store",
    data: {
      dashboardSettings: (state) =>
        state?.accountId === accountId
          ? {
              ...state,
              language,
              tab,
              companies,
              dialog: undefined,
              selected: state.tab === tab ? state.selected : undefined,
            }
          : {
              accountId,
              language,
              tab,
              search: new StringFieldModel(),
              companies,
              previewOrgNo: companies[0]?.orgNo,
              previewAsOf: asOfDateModel(),
              definitions: [],
              roleTypes: [],
              mapTemplates: [],
              previewData: {},
            },
    },
  },
  dashboardApiClient.overview(accountId, {
    onSuccess: (res) => [
      updateDashboardSettings(accountId, { overview: res.data }),
      setMapTemplates(accountId, hasTemplates ? res.data.mapTemplates : []),
    ],
    onFailure: () => [showGeneralError()],
  }),
  customFieldsApiClient.definitions(accountId, {
    onSuccess: (res) => [updateDashboardSettings(accountId, { definitions: res.data })],
    onFailure: () => [showGeneralError()],
  }),
  accountRoleTypeApiClient.list(accountId, {
    onSuccess: (res) => [
      setRoleTypes(
        accountId,
        res.data.map((roleType) => ({ id: roleType.id, name: roleType.name })),
      ),
    ],
    onFailure: () => [showGeneralError()],
  }),
];

export const updateDashboardSettings = (
  accountId: string,
  partial: Partial<DashboardSettingsState>,
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    dashboardSettings: (state) =>
      state?.accountId === accountId ? { ...state, ...partial } : state,
  },
});

const setRoleTypes = (accountId: string, roleTypes: AccountRoleType[]): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    dashboardSettings: (state) =>
      state?.accountId === accountId
        ? { ...state, roleTypes, dialog: state.dialog && withRoleTypes(state.dialog, roleTypes) }
        : state,
  },
});

const setMapTemplates = (accountId: string, mapTemplates: TemplateDTO[]): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    dashboardSettings: (state) =>
      state?.accountId === accountId
        ? {
            ...state,
            mapTemplates,
            dialog: state.dialog && withMapTemplates(state.dialog, mapTemplates),
          }
        : state,
  },
});

export const dashboardSettingsTitle: { [key in DashboardSettingsTab]: TMessageKey } = {
  dashboards: "enterprise-account.dashboards",
  cards: "enterprise-account.dashboards.tab.cards",
};

export const prepDashboardSettingsSearch = (state: DashboardSettingsState): MTextInputProps => ({
  model: state.search,
  placeholder: { key: "search" },
  icon: "magnifying-glass",
  type: "search",
  inline: true,
});

const illustration = (src: string) => createElement("img", { src, alt: "" });

export const prepDashboardSettingsEmptyState = (
  state: DashboardSettingsState,
  aiClient: AIClient,
): EmptyStateProps | undefined => {
  const overview = state.overview;
  if (!overview) return undefined;
  const newCard: ButtonProps = {
    variant: "primary",
    icon: "plus",
    label: { key: "enterprise-account.dashboards.new-card" },
    onClick: openCardDialog(state, aiClient),
  };
  if (overview.cards.length === 0) {
    return {
      illustration: illustration(
        state.tab === "cards" ? noCardsIllustration : noDashboardsIllustration,
      ),
      title: {
        key:
          state.tab === "cards"
            ? "enterprise-account.dashboards.cards.empty"
            : "enterprise-account.dashboards.empty",
      },
      description: { key: "enterprise-account.dashboards.cards.empty.description" },
      actions: [{ type: "button", key: "new-card", props: newCard }],
    };
  }
  if (state.tab === "cards" || overview.dashboards.length > 0) return undefined;
  return {
    illustration: illustration(noDashboardsIllustration),
    title: { key: "enterprise-account.dashboards.empty" },
    actions: [
      {
        type: "button",
        key: "new-dashboard",
        props: { ...prepNewButton(state, aiClient), variant: "primary" },
      },
      {
        type: "button",
        key: "manage-cards",
        props: {
          variant: "secondary",
          icon: "squares-four",
          label: { key: "enterprise-account.dashboards.manage-cards" },
          onClick: [navigate(`/enterprise-account/${state.accountId}/settings/dashboardCards`)],
        },
      },
    ],
  };
};

const statusLabels: { [key in DashboardStatus]: TMessageKey } = {
  ENABLED: "enabled",
  DISABLED: "disabled",
};

export const dashboardStatusLabel = ({ status }: { status: DashboardStatus }): TMessage => ({
  key: statusLabels[status],
});

export const dashboardVisibleToLabel = (
  overview: DashboardOverview,
  dashboard: Dashboard,
): TMessage | TMessageParam => {
  const groups = overview.groups.filter((group) => dashboard.visibleTo.includes(group.grantee));
  return groups.length === 0
    ? { key: "none" }
    : { type: "list", value: groups.map(accountUserGroupLabel) };
};

export const dashboardUpdatedLabel = (dashboard: Dashboard): TMessageParam => ({
  type: "date",
  value: dashboard.updatedAt,
});

export const cardUsageLabel = (card: DashboardCardDefinition): TMessage =>
  card.dashboardCount === 0
    ? { key: "enterprise-account.dashboards.cards.not-in-use" }
    : {
        key: "enterprise-account.dashboards.cards.used-in",
        params: { count: card.dashboardCount },
      };

export const cardTypeLabel = (card: DashboardCardDefinition): TMessage =>
  cardTypeLabelFor(card.type);

export const cardCreatedLabel = (card: DashboardCardDefinition): TMessageParam => ({
  type: "date",
  value: card.createdAt,
});

export const prepNewButton = (state: DashboardSettingsState, aiClient: AIClient): ButtonProps => ({
  variant: "secondary",
  icon: "plus",
  label: {
    key:
      state.tab === "dashboards"
        ? "enterprise-account.dashboards.new-dashboard"
        : "enterprise-account.dashboards.new-card",
  },
  onClick:
    state.tab === "dashboards"
      ? openDashboardDialog(state, aiClient)
      : openCardDialog(state, aiClient),
});

export type DashboardRow =
  | { kind: "system-default"; status: DashboardStatus }
  | { kind: "dashboard"; dashboard: Dashboard };

export const dashboardRows = (overview: DashboardOverview): DashboardRow[] => [
  { kind: "system-default", status: overview.systemDefaultStatus },
  ...overview.dashboards.map((dashboard): DashboardRow => ({ kind: "dashboard", dashboard })),
];

export const dashboardRowName = (row: DashboardRow, t: (message: TMessage) => string): string =>
  row.kind === "dashboard"
    ? row.dashboard.internalName
    : t({ key: "enterprise-account.dashboards.system-default" });

export const systemDefaultVisibleToLabel = (status: DashboardStatus): TMessage => ({
  key: status === "ENABLED" ? "enterprise-account.dashboards.all-users" : "none",
});

export const setSystemDefaultStatus = (
  state: DashboardSettingsState,
  status: DashboardStatus,
): DCEvent[] => [
  dashboardApiClient.setSystemDefaultStatus(state.accountId, status, {
    onSuccess: (res) => [updateDashboardSettings(state.accountId, { overview: res.data })],
    onFailure: () => [showGeneralError()],
  }),
];

export const prepSystemDefaultActions = (
  state: DashboardSettingsState,
  status: DashboardStatus,
): RowAction[] => [
  [
    "context-menu",
    [
      status === "ENABLED"
        ? {
            type: "item",
            icon: "eye-closed",
            label: { key: "enterprise-account.dashboards.disable" },
            onSelect: setSystemDefaultStatus(state, "DISABLED"),
          }
        : {
            type: "item",
            icon: "eye",
            label: { key: "enterprise-account.dashboards.enable" },
            onSelect: setSystemDefaultStatus(state, "ENABLED"),
          },
    ],
  ],
];

export const prepDashboardActions = (
  state: DashboardSettingsState,
  aiClient: AIClient,
  dashboard: Dashboard,
): RowAction[] => [
  [
    "context-menu",
    [
      {
        type: "item",
        icon: "edit",
        label: { key: "edit" },
        onSelect: openDashboardDialog(state, aiClient, dashboard),
      },
      {
        type: "item",
        icon: "trash",
        label: { key: "settings.delete" },
        color: "error",
        onSelect: openDeleteDialog(state, { kind: "dashboard", dashboard }),
      },
    ],
  ],
];

export const prepCardActions = (
  state: DashboardSettingsState,
  aiClient: AIClient,
  card: DashboardCardDefinition,
): RowAction[] => [
  [
    "context-menu",
    [
      {
        type: "item",
        icon: "edit",
        label: { key: "edit" },
        onSelect: openCardDialog(state, aiClient, card),
      },
      {
        type: "item",
        icon: "trash",
        label: { key: "settings.delete" },
        color: "error",
        onSelect: openDeleteDialog(state, { kind: "card", card }),
      },
    ],
  ],
];
